// Per-browser UI preferences, kept in localStorage.
// Every read/write is guarded: storage can be unavailable (private mode, blocked site data).
const KEYS = {
  sidebarCollapsed: '2way.sidebarCollapsed',
  themeMode: '2way.themeMode',
  popupPreviewBackdrop: '2way.popupPreviewBackdrop',
  locale: '2way.locale',
  startPage: '2way.startPage',
  languageSuggestionDismissed: '2way.languageSuggestionDismissed',
  enterToSend: '2way.enterToSend',
};
export const UI_PREFERENCES_EVENT = '2way:ui-preferences';

const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (e) {
    // ignore: the preference just won't persist
  }
  window.dispatchEvent(new CustomEvent(UI_PREFERENCES_EVENT, {detail: {key, value}}));
};

export const getSidebarCollapsed = () => read(KEYS.sidebarCollapsed) === 'true';
export const setSidebarCollapsed = (collapsed) => write(KEYS.sidebarCollapsed, String(!!collapsed));

// 'light' | 'dark'. Nothing stored yet: follow the operating system.
export const getThemeMode = () => {
  const stored = read(KEYS.themeMode);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};
export const setThemeMode = (mode) => write(KEYS.themeMode, mode === 'dark' ? 'dark' : 'light');

// Backdrop behind the popup preview: 'site' (screenshot of the customer's website), 'light' or 'dark'.
export const getPopupPreviewBackdrop = () => {
  const stored = read(KEYS.popupPreviewBackdrop);
  return ['site', 'light', 'dark'].includes(stored) ? stored : 'site';
};
export const setPopupPreviewBackdrop = (value) => write(KEYS.popupPreviewBackdrop, value);
// What the user picked: 'light' | 'dark' | 'auto' (follow the operating system).
export const getThemePreference = () => {
  const stored = read(KEYS.themeMode);
  return stored === 'light' || stored === 'dark' ? stored : 'auto';
};
export const setThemePreference = (preference) =>
  write(KEYS.themeMode, preference === 'light' || preference === 'dark' ? preference : null);

// Calls back when the OS switches light/dark, so 'auto' follows it live. Returns an unsubscribe function.
export const watchSystemTheme = (callback) => {
  const query = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
  if (!query) return () => {};
  const handler = () => callback();
  if (query.addEventListener) query.addEventListener('change', handler);
  else if (query.addListener) query.addListener(handler);
  return () => {
    if (query.removeEventListener) query.removeEventListener('change', handler);
    else if (query.removeListener) query.removeListener(handler);
  };
};

// Interface language picked in the account menu ('en' | 'pl' | 'es' …); null when never picked.
export const getStoredLocale = () => read(KEYS.locale);
export const setStoredLocale = (locale) => write(KEYS.locale, locale);

// Page to open after login, cached here so the redirect is instant; the account copy is on the server.
export const getStoredStartPage = () => read(KEYS.startPage);
export const setStoredStartPage = (path) => write(KEYS.startPage, path || null);

// Language the "switch to your browser language?" bar was closed for (so it does not come back).
export const getLanguageSuggestionDismissed = () => read(KEYS.languageSuggestionDismissed);
export const setLanguageSuggestionDismissed = (locale) => write(KEYS.languageSuggestionDismissed, locale);

export const applyThemeModeToDocument = (mode) => {
  document.documentElement.setAttribute('data-theme', mode);
};

// Chat composer: does Enter send the message (default) or add a new line?
// null when this browser has no choice yet (the account copy may have one).
export const getStoredEnterToSend = () => {
  const value = read(KEYS.enterToSend);
  return value === null ? null : value === 'true';
};
export const setStoredEnterToSend = (value) => write(KEYS.enterToSend, String(!!value));
